import {AfterViewInit, Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Dictionary} from 'highcharts/highcharts.src';
import {FormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import {UsersService} from '../../../services/users/users.service';
import {MatTableDataSource} from '@angular/material/table';
import {TeamsService} from '../../../services/teams/teams.service';
import {AppSettings} from '../../../app.settings';
import {MatSort} from '@angular/material/sort';
import {fromEvent} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, tap} from 'rxjs/operators';
import {ClientsInterface} from '../../clients/clients.interface';

@Component({
    selector: 'app-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportsComponent implements OnInit, AfterViewInit {

  @ViewChild('search') search: ElementRef | undefined;
  @ViewChild(MatSort) sort!: MatSort;

  public form;
  public loading = false;
  public partners = Array<Dictionary<any>>();
  public teams = Array<Dictionary<any>>();
  public items = new MatTableDataSource<any>();
  public columnsToDisplay = ['partnerName', 'teamName', 'name', 'createdAt', 'lastLogin', 'loginsTotalYearMonth', 'productsEnrolledPurchased', 'totalPremium'];
  public isAdmin = this.usersService.isAdmin();
  public loadingAll = false;

  private partner?: Dictionary<any>;
  private team?: Dictionary<any>;

  private pageNumber = 0;
  private pageSize = 100;
  private atEnd = false;
  private searchValue = '';

  constructor(
      private formBuilder: FormBuilder,
      private usersService: UsersService,
      private teamsService: TeamsService
  ) {
    this.form = this.formBuilder.group({
      partner: new UntypedFormControl(0, [Validators.required]),
      team: new UntypedFormControl(0, [Validators.required])
    });
  }

  ngOnInit(): void {
    this.usersService.getVisibleBrands().then(partners => {
      this.partners = partners;

      // Preserve your original brand override logic (even though you weren't using brandSlug later)
      const brandSlug = AppSettings.getBrandOverride() || this.usersService.me().user.brand || 'wallit';
      void brandSlug;

      // Initialize partner selection to current user brand if possible
      const initialPartnerIndex = this.getInitialPartnerIndex();
      if (initialPartnerIndex > 0) {
        this.form.get('partner')?.setValue(initialPartnerIndex, { emitEvent: false });
        this.partner = this.partners[initialPartnerIndex - 1];
      }

      this.loadTeams();
      this.loadMoreItems(true);
    });

    this.form.get('partner')?.valueChanges.subscribe(value => {
      if (!this.partners || this.partners.length === 0) {
        return;
      }
      this.partner = value ? this.partners[value - 1] : undefined;
      // Team list depends on partner; reset team selection without re-triggering team valueChanges.
      this.form.get('team')?.setValue(0, { emitEvent: false });
      this.team = undefined;
      this.loadTeams();
      this.loadMoreItems(true);
    });

    this.form.get('team')?.valueChanges.subscribe(value => {
      if (!this.teams || this.teams.length === 0) {
        this.team = undefined;
        this.loadMoreItems(true);
        return;
      }
      this.team = value ? this.teams[value - 1] : undefined;
      this.loadMoreItems(true);
    });
  }

  ngAfterViewInit(): void {
    this.items.sort = this.sort;
    this.items.sortingDataAccessor = (item, property) => this.getSortValue(item, property);

    fromEvent<KeyboardEvent>(this.search?.nativeElement, 'keyup')
        .pipe(
            map(event => (event.target as HTMLInputElement).value),
            debounceTime(300),
            distinctUntilChanged(),
            tap(value => {
              this.searchValue = (value || '').toString();
              this.loadMoreItems(true);
            })
        )
        .subscribe();
  }

  private getSortValue(item: Dictionary<any>, property: string): string | number {
    switch (property) {
      case 'partnerName':
        return (item?.partnerName || '').toString().toLowerCase();
      case 'teamName':
        return (item?.teamName || '').toString().toLowerCase();
      case 'name': {
        const first = (item?.firstName || '').toString();
        const last = (item?.lastName || '').toString();
        return `${first} ${last}`.trim().toLowerCase();
      }
      case 'createdAt':
        return this.toTimestamp(item?.createdAt);
      case 'lastLogin':
        return this.toTimestamp(item?.lastLoginDate);
      case 'loginsTotalYearMonth':
        return this.toNumber(item?.lifetimeLoginCount);
      case 'productsEnrolledPurchased':
        return this.toNumber(item?.activePostWithoutStripeCount) + this.toNumber(item?.stripeSubscriptionCount);
      case 'totalPremium':
        return this.toNumber(item?.totalMonthlyAmount);
      default: {
        const value = item?.[property];
        if (typeof value === 'number') {
          return value;
        }
        return (value || '').toString().toLowerCase();
      }
    }
  }

  private toTimestamp(value: any): number {
    if (!value) {
      return 0;
    }
    const ts = new Date(value).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private getInitialPartnerIndex(): number {
    // Returns i+1 as used by your mat-option values, or 0 for "All Partners".
    try {
      const me = this.usersService.me();
      const brandSlug = AppSettings.getBrandOverride() || me?.user?.brand || '';
      if (!brandSlug) {
        return 0;
      }

      // partners returned by getVisibleBrands() appear to have shortName like "wallit.xxx" sometimes.
      const idx = this.partners.findIndex(p => {
        const shortName = (p?.shortName || '').toString();
        const slugFromShort = shortName.split('.')[0];
        return slugFromShort === brandSlug;
      });

      return idx >= 0 ? (idx + 1) : 0;
    } catch (e) {
      return 0;
    }
  }

  private loadTeams(): void {
    if (this.isAdmin) {
      this.teamsService.getAllTeams().then(teams => {
        this.teams = teams || [];
      });
      return;
    }

    const partnerId = this.partner?.partnerId || '';
    this.teamsService.getTeamsForBrand(partnerId).then(teams => {
      this.teams = teams || [];
    });
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    const threshold = 150;
    if (scrollTop + offsetHeight >= scrollHeight - threshold) {
      this.loadMoreItems(false);
    }
  }

  loadMoreItems(clear = false): void {
    if (clear) {
      this.atEnd = false;
      this.pageNumber = 0;
      this.items.data = [];
    }

    if (this.loading || this.loadingAll || this.atEnd) {
      return;
    }

    this.loading = true;
    this.pageNumber++;

    const limit = this.pageSize;

    const teamId = this.team?.id || '';
    const partnerId = this.partner?.partnerId || '';

    this.teamsService
        .getTeamMembersLoginInfo(teamId, partnerId, this.searchValue, this.pageNumber - 1, limit)
        .then(members => {
          const rows = members || [];
          if (rows.length === 0 || rows.length < limit) {
            this.atEnd = true;
          }
          this.items.data = this.items.data.concat(rows);
        })
        .catch(() => {
          // If the call fails, don't permanently block scrolling.
          this.atEnd = false;
        })
        .finally(() => {
          this.loading = false;
        });
  }

  async loadAllItems(clear = false): Promise<void> {
    if (this.loading || this.loadingAll) {
      return;
    }

    if (clear) {
      this.atEnd = false;
      this.pageNumber = 0;
      this.items.data = [];
    }

    this.loadingAll = true;

    const teamId = this.team?.id || '';
    const partnerId = this.partner?.partnerId || '';
    const limit = this.pageSize;

    try {
      while (!this.atEnd) {
        this.pageNumber++;
        // eslint-disable-next-line no-await-in-loop
        const members = await this.teamsService.getTeamMembersLoginInfo(
            teamId,
            partnerId,
            this.searchValue,
            this.pageNumber - 1,
            limit
        );
        const rows = members || [];
        this.items.data = this.items.data.concat(rows);
        if (rows.length === 0 || rows.length < limit) {
          this.atEnd = true;
        }
      }
    } catch (e) {
      this.atEnd = false;
    } finally {
      this.loadingAll = false;
    }
  }

  get totalMembers(): number {
    return this.items.data.length;
  }

  get totalPremium(): number {
    return this.items.data.reduce((sum, row) => sum + this.toNumber(row?.totalMonthlyAmount), 0);
  }

  get totalEnrolled(): number {
    return this.items.data.reduce((sum, row) => sum + this.toNumber(row?.activePostWithoutStripeCount), 0);
  }

  get totalPurchased(): number {
    return this.items.data.reduce((sum, row) => sum + this.toNumber(row?.stripeSubscriptionCount), 0);
  }

  get totalLogins(): number {
    return this.items.data.reduce((sum, row) => sum + this.toNumber(row?.lifetimeLoginCount), 0);
  }

  getFilteredData(data: ClientsInterface[]): Partial<ClientsInterface>[] {
    return data; // data.map(({ name, email, sic, ein }) => ({ name, email, sic, ein }));
  }


  async exportCsv(): Promise<void> {
    await this.loadAllItems(true);
    const filteredData: Partial<ClientsInterface>[] = this.getFilteredData(this.items.data);
    const csvContent: string = this.convertToCsv(filteredData);
    this.downloadCsv(csvContent, 'Teams_activity.csv');
  }

  convertToCsv(data: any[]): string {
    const header: string[] = ['partnerName', 'teamName', 'firstName', 'lastName', 'createdAt', 'lifetimeLoginCount', 'thisYearLoginCount', 'thisMonthLoginCount', 'productsEnrolled', 'productsPurchased', 'totalPremium'];
    const rows: string[] = data.map(row => header.map((col: string) => row[col]).join(','));
    return [header.join(','), ...rows].join('\n');
  }

  downloadCsv(csvContent: string, filename: string): void {
    const blob: Blob = new Blob([csvContent], { type: 'text/csv' });
    const url: string = window.URL.createObjectURL(blob);
    const a: HTMLAnchorElement = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
